import React, { useEffect, useState } from 'react';
import { createFormBag, FormData, FormBag, FormErrors, validateFormBag, FormUpdateEvent } from 'react-formage'
import { Input } from 'components/ui/input'

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { GenericPointOfInterest } from 'generated/mos/org/getty/audio'
import { CreateGenericPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'

import { bffEndpoint } from 'helpers/bff'
import { RadioInputElement } from 'components/ui/radio'
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { AttributeRef } from 'generated/mos/entity'
import { Media } from 'generated/mos/media'
import { genericPointOfInterestSchema } from '../../../schemas/generic-point-of-interest'
import { Category } from 'generated/mos/org/getty/pointofinterest'

import { CATEGORY_TITLES } from '../list-item'

export type FormValues = {
  title: string;
};

export const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values.title.trim()) {
    errors.title = 'Title required';
  }
  return errors;
};

type ConnectedProps = {
  genericPointOfInterestCreate: GettyStore['getty']['genericPointOfInterestCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateGenericPointOfInterestRequest' | 'gettyCreateGenericPointOfInterestReset'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  onCancel: () => void;
  onComplete: (pointOfInterest: GenericPointOfInterest.Entity) => void;
  moodRef: CreateGenericPointOfInterestRequest.Entity['moodRef'];
  projectId: string;
  historyPush: (id: string) => void;
};

type Props = ConnectedProps & ActionProps & DirectProps;

const GenericPointOfInterestCreateView = (props: Props) => {
  const {
    gettyCreateGenericPointOfInterestRequest,
    gettyCreateGenericPointOfInterestReset,
    onComplete,
    genericPointOfInterestCreate,
    toastNotification,
    projectId,
    historyPush,
  } = props

  const [ category, setCategory ] = useState<Category>('PRO_TIP')

  const categoryOption = (key: Category, name: string) => {
    return (
      <ListItemLabel key={key}>
        <RadioInputElement checked={category == key} name={key} onChange={event => setCategory(key)} />
        <Description>
          <Title>{name}</Title>
        </Description>
      </ListItemLabel>
    );
  }

  const createPointOfInterest = () => {
    const validatedBag = validateFormBag(bag, validate)
    setFormBag(validatedBag)

    if (genericPointOfInterestCreate.status === Status.Idle && validatedBag.valid && category != 'UNSET') {
      gettyCreateGenericPointOfInterestRequest({
        ...CreateGenericPointOfInterestRequest.defaults,
        title: genericPointOfInterestSchema.title ? genericPointOfInterestSchema.title.write(bag.values.title) : bag.values.title,
	category: category,
        type: CreateGenericPointOfInterestRequest.refName,
        moodRef: props.moodRef
      })
    }

    if (genericPointOfInterestCreate.status === Status.Failed) {
      toastNotification({
        type: 'error',
        text: 'Could not create pointOfInterest',
      });
      gettyCreateGenericPointOfInterestReset()
    }
  }

  useEffect(() => {
    if (genericPointOfInterestCreate.status === Status.Ready) {
      onComplete(genericPointOfInterestCreate.data);
      gettyCreateGenericPointOfInterestReset();
    }
  }, [ genericPointOfInterestCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  const [ bag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ title: '',  }))

  const onUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)
  }

  return (
    <Modal
      onClose={props.onCancel}
      header={<h3>Create Point of Interest</h3>}
      footer={(
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button onClick={createPointOfInterest} >
            Create Point of Interest
          </Button>
        </ButtonGroup>
      )}
    >
      <StatusGuard status={genericPointOfInterestCreate} mode="form">
        <form noValidate onSubmit={event => {event.preventDefault()}}>
          <FormData bag={bag} onUpdate={onUpdate} validate={validate}>
            <Input
              label="Title"
              field="title"
              error={bag.touched.title ? bag.errors.title : undefined}
          />
            {categoryOption("PRO_TIP", CATEGORY_TITLES["PRO_TIP"])}
            {categoryOption("ACTIVITY", CATEGORY_TITLES["ACTIVITY"])}
            {categoryOption("VIEW", CATEGORY_TITLES["VIEW"])}
          </FormData>
        </form>
      </StatusGuard>
    </Modal>
  );
};

export const GenericPointOfInterestCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => ({
    genericPointOfInterestCreate: store.getty.genericPointOfInterestCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateGenericPointOfInterestRequest', 'gettyCreateGenericPointOfInterestReset'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(GenericPointOfInterestCreateView)
